import ApiHelpers from '@/data/dao/base/ApiHelpers';
import Meter from '../models/meter';
import MeterReadingCsvMapping from '@/data/models/meter-reading-csv-mapping';
import MeterModelForm from '@/data/models/meter-model-form';
import MeterIdentificationResponses from '@/data/models/meterModelResponse';
import MeterReading from "@/data/models/meter-reading";
import dayjs from "dayjs";

export default class MetersDao {
  static async getAllByBuilding(buildingId: string): Promise<Array<Meter>> {
    return Promise.all(await ApiHelpers.get<Array<Meter>>(`/meter/${buildingId}`).then((data) => data.map(async it => {
      if (it.referenceMeterId != null) {
        return await this.getById(it.referenceMeterId).then((meter) => {
          meter.original = Meter.map(it);
          return meter;
        });
      } else return Meter.map(it);
    })));
  }

  static getAll(): Promise<Array<Meter>> {
    return ApiHelpers.get<Array<Meter>>('/admin/meter/all').then((data) => data.map(it => Meter.map(it)));
  }

  static getLastValue(meterId: string): Promise<MeterReading | null> {
    return ApiHelpers.get<MeterReading>(`/admin/meter/getLastValue/${meterId}`).then((data) => MeterReading.map(data));
  }

  static getLastValues(meterIds: string[]): Promise<MeterReading[]> {
    return ApiHelpers.get<MeterReading[]>(`/admin/meter/getLastValues?ids[]=${meterIds.join('&ids[]=')}`).then((data) => data.map(it => MeterReading.map(it)));
  }

  static getById(meterId: string): Promise<Meter> {
    return ApiHelpers.get<Meter>(`/admin/meter/${meterId}`).then((data) => Meter.map(data));
  }

  static getStructuresById(meterId: string): Promise<Array<MeterReadingCsvMapping>> {
    return ApiHelpers.get<Array<MeterReadingCsvMapping>>(`/admin/csv/${meterId}`).then(data => data.map(it => MeterReadingCsvMapping.map(it)));
  }

  static create(meter: Meter): Promise<Meter> {
    return ApiHelpers.post<Meter>(`/admin/meter/create/${meter.buildingId}`, meter.toJSON(true)).then((data) => Meter.map(data));
  }

  static update(meter: Meter, id: string): Promise<Meter> {
    return ApiHelpers.patch<Meter>(`/admin/meter/update/${id}`, meter.toJSON()).then((data) => Meter.map(data));
  }

  static createCSVStructure(meterReading: MeterReadingCsvMapping): Promise<MeterReadingCsvMapping> {
    return ApiHelpers.post<MeterReadingCsvMapping>(`/admin/csv/create/${meterReading.meterId}`, meterReading.toJSON()).then((data) => MeterReadingCsvMapping.map(data));
  }

  static updateCSVStructure(meterReading: MeterReadingCsvMapping): Promise<MeterReadingCsvMapping> {
    return ApiHelpers.patch<MeterReadingCsvMapping>(`/admin/csv/update/${meterReading.id}`, meterReading.toJSON()).then((data) => MeterReadingCsvMapping.map(data));
  }

  static deleteCSVStructure(meterReadingId: string): Promise<string> {
    return ApiHelpers.delete(`/admin/csv/delete/${meterReadingId}`).then((result) => result);
  }

  static importCSV(filename: string, csv: string): Promise<{ inserted: string, total: string, details: { string: any } }> {
    return ApiHelpers.post<{ inserted: string, total: string, details: { string: any } }>(`/import/batch/${filename}`, csv);
  }

  static getReferencesMeters(): Promise<Meter[]> {
    return ApiHelpers.get<Meter[]>(`/admin/meter/references`).then(data => {
      return data.map(it => Meter.map(it));
    });
  }

  static getMeterIdentifications(meterId: string): Promise<[MeterIdentificationResponses]> {
    return ApiHelpers.get<[MeterIdentificationResponses]>(`/admin/meter/identification/${meterId}`);
  }

  static requestComputeIdentification(meterModelForm: Partial<MeterModelForm>): Promise<[]> {
    return ApiHelpers.post<[]>(`/admin/meter/compute-identification/${meterModelForm.buildingId}/${meterModelForm.meterId}`, meterModelForm);
  }

  /**
   * Obtain the JSON normally processed by Scilab
   * @param meterModelForm
   */
  static getRequestComputeIdentificationJSON(meterModelForm: Partial<MeterModelForm>): Promise<any> {
    return ApiHelpers.post(`/admin/meter/get-compute-identification/${meterModelForm.buildingId}/${meterModelForm.meterId}`, meterModelForm);
  }


  /**
   * Obtain the JSON normally processed by Scilab
   */
  static getModelUpdateJSON(meterModelForm: Partial<MeterModelForm>): Promise<any> {
    return ApiHelpers.post(`/admin/meter/generateJsonForComputationUpdate/${meterModelForm.buildingId}/${meterModelForm.meterId}`, meterModelForm);
  }

  /**
   * Request a Scilab computation for the meter
   */
  static triggerComputationUpdateForMeter(meterModelForm: Partial<MeterModelForm>): Promise<any> {
    return ApiHelpers.post(`/admin/meter/triggerComputationUpdateForMeter/${meterModelForm.buildingId}/${meterModelForm.meterId}`, meterModelForm);
  }

  static downloadRawData(meterId: string, startDate: dayjs.Dayjs, endDate: dayjs.Dayjs): Promise<Blob> {
    // YYYY-MM-DD date format
    return ApiHelpers.getRaw<string>(`/admin/meter/download/${meterId}?startDate=${startDate.format('YYYY-MM-DD')}&endDate=${endDate.format('YYYY-MM-DD')}`)
      .then((rep) => rep.blob());
  }

  static archive(id: string): Promise<any> {
    return ApiHelpers.delete(`/admin/meter/delete/${id}`);
  }

  static importFromCSV(buildingId: string, text: string): Promise<unknown> {
    return ApiHelpers.post(`/admin/meter/create-from-csv/${buildingId}`, text);
  }

  static getCountValues(meterId: string): Promise<{ count: number }> {
    return ApiHelpers.get<{ count: number }>(`/admin/meter/getValueCountForMeter/${meterId}`);
  }

  static deleteMeterReadings(meterId: string, cleanStartDate: string): Promise<any> {
    if (cleanStartDate === undefined || cleanStartDate === null || cleanStartDate === "" || meterId === undefined || meterId === null || meterId === "") {
      throw new Error("cleanStartDate and meterId are required");
    }

    return ApiHelpers.delete(`/admin/meter/removeMeterReadingsSince/${meterId}/${cleanStartDate}`);

  }

  // /removeMeterReadingsBetween/:meterId/:startDate/:endDate
  static deleteMeterReadingsInterval(meterId: string, startDate: string, endDate: string): Promise<any> {
    if (startDate === undefined || startDate === null || startDate === "" || meterId === undefined || meterId === null || meterId === "") {
      throw new Error("startDate and meterId are required");
    }

    if (endDate === undefined || endDate === null || endDate === "") {
      throw new Error("endDate is required");
    }

    return ApiHelpers.delete(`/admin/meter/removeMeterReadingsBetween/${meterId}/${startDate}/${endDate}`);
  }

  static evaluateDriftFormula(meterId: string, formula: string): Promise<any> {
    return ApiHelpers.post(`/admin/meter/evaluate-drift/${meterId}`, { formula });
  }

  static downloadDerive(meterId: string): Promise<Blob> {
    return ApiHelpers.getRaw<string>(`/admin/meter/getDriftValuesCSV/${meterId}`)
      .then((rep) => rep.blob());
  }

  static moveMeterToBuilding(meterId: string, id: string): Promise<boolean> {
    // POST admin/meter/move/:meterId/:buildingId
    return ApiHelpers.post<boolean>(`/admin/meter/move/${meterId}/${id}`, {}).then((result) => result);
  }
}
